// App.js
import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import {
  FaPlay,
  FaStop,
  FaDownload,
  FaVolumeUp,
  FaUserMd,
  FaHospital,
  FaBuilding,
  FaHeart,
  FaChild,
  FaRobot,
  FaSync,
  FaCheckCircle,
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';

function App() {
  const [doctorName, setDoctorName] = useState('Dr. Rajesh Sharma');
  const [brandName, setBrandName] = useState('Apollo Children\'s Hospital');
  const [divisionName, setDivisionName] = useState('Pediatric Care Division');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  // Audio URL - only set after AI generation
  const [audioUrl, setAudioUrl] = useState('');
  const [audioIsObjectUrl, setAudioIsObjectUrl] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [isCreatingVideo, setIsCreatingVideo] = useState(false);
  const [aiStatus, setAiStatus] = useState('ready');
  const [errorMessage, setErrorMessage] = useState('');
  const [generationId, setGenerationId] = useState('');

  const audioRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Whether we have AI-generated audio available
  const canCreateVideo = () => {
    return !!audioBlob;
  };

  // ElevenLabs API Configuration - Only Indian Voice
  const ELEVENLABS_API_KEY = 'sk_9e48e9c2e381c5b5b9a036e9e18ae8cc462c5ca33d3dc03d';//'sk_ebce1c68d1ab90b5106695733130b406262c33d646aed355';
  const INDIAN_VOICE_ID = 'HobRzuqtLputbKAXOdTj'; // Indian English voice ID

  // Generate message text
  const generateMessage = () => {
    return `Dear ${doctorName},

On this Pediatrician's Day, we extend our heartfelt gratitude for the exceptional care, compassion, and dedication you bring to every child's life.

Your unwavering commitment shapes healthier futures, one precious smile at a time.

Warm wishes on Pediatrician's Day,
from ${brandName}, ${divisionName}.`;
  };

  // Clean up audio URL to prevent memory leaks
  const cleanupAudio = () => {
    if (audioIsObjectUrl && audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl('');
    setAudioIsObjectUrl(false);
    if (audioRef.current) {
      audioRef.current.src = '';
    }
    setAudioBlob(null);
  };

  // Clean up video URL
  const cleanupVideo = () => {
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
      setVideoUrl('');
    }
    setVideoBlob(null);
  };

  // Create a single-frame video (canvas) with the message text and AI-generated audio
  const createVideoFromAudio = async (text) => {
    // Only allow video creation with AI-generated audio
    if (!audioRef.current || !audioRef.current.src) {
      setErrorMessage('Please generate AI audio first');
      return;
    }

    setIsCreatingVideo(true);
    setErrorMessage('');

    try {
      // Canvas setup
      const canvas = document.createElement('canvas');
      const width = 1350;
      const height = 1080;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      // Try to load the provided frame image from public/frame.png
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = 'frame.jpg';
      try {
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        ctx.drawImage(img, 0, 0, width, height);
      } catch (err) {
        // Fallback: solid background if image not available
        console.log('Frame image not found, using solid background');
        ctx.fillStyle = '#0b1020';
        ctx.fillRect(0, 0, width, height);
      }

      // Overlay dynamic text onto the frame
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'left';

      // Draw "Dear {DoctorName}," near the top
      ctx.font = '60px Montserrat extrabold';
      const leftMargin = 90;
      const dearY = 400;
      ctx.fillText(`${doctorName},`, leftMargin, dearY);

      // Draw the closing signature (hospital and division) near the bottom
      ctx.font = '36px Montserrat bold';
      const sigY1 = height - 190;
      const sigY2 = height - 160;
      ctx.fillText(brandName, leftMargin, sigY1);
      ctx.fillText(divisionName, leftMargin, sigY2);

      // Capture canvas stream
      const fps = 30;
      const canvasStream = canvas.captureStream(fps);

      // Prepare audio
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioCtx();
      const dest = audioContext.createMediaStreamDestination();

      // Use AI-generated audio from audio element
      const audioEl = audioRef.current;
      if (!audioEl) throw new Error('Audio element not ready');

      // Wait for audio to be ready
      await new Promise((resolve, reject) => {
        if (audioEl.readyState >= 1) return resolve();

        const onLoaded = () => {
          cleanupListeners();
          resolve();
        };
        const onErr = (e) => {
          cleanupListeners();
          reject(new Error('Failed to load audio metadata'));
        };

        const timeout = setTimeout(() => {
          cleanupListeners();
          resolve();
        }, 5000);

        const cleanupListeners = () => {
          clearTimeout(timeout);
          audioEl.removeEventListener('loadedmetadata', onLoaded);
          audioEl.removeEventListener('error', onErr);
        };

        audioEl.addEventListener('loadedmetadata', onLoaded);
        audioEl.addEventListener('error', onErr);
      });

      // Create media element source from the audio element
      const sourceNode = audioContext.createMediaElementSource(audioEl);
      sourceNode.connect(dest);
      sourceNode.connect(audioContext.destination);

      // Combine video and audio streams
      const combinedStream = new MediaStream();
      canvasStream.getVideoTracks().forEach(t => combinedStream.addTrack(t));
      dest.stream.getAudioTracks().forEach(t => combinedStream.addTrack(t));

      // Configure recorder
      const options = { mimeType: 'video/webm; codecs=vp8,opus' };
      const recorder = new MediaRecorder(combinedStream, options);

      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      const stopped = new Promise((resolve) => {
        recorder.onstop = resolve;
      });

      // Get audio duration from audio element
      const duration = audioRef.current.duration || 5;

      // Start recording
      recorder.start();

      // Reset audio and play it during recording
      audioEl.currentTime = 0;
      try {
        await audioEl.play();
      } catch (e) {
        console.warn('Audio play during recording:', e);
      }

      // Wait for recording to complete (duration + buffer)
      const waitMs = Math.max(1000, Math.ceil(duration * 1000) + 500);
      await new Promise(r => setTimeout(r, waitMs));

      recorder.stop();
      await stopped;

      // Close audio context to prevent memory leaks
      audioContext.close();

      // Create and set video blob
      const videoBlob = new Blob(chunks, { type: 'video/webm' });
      cleanupVideo(); // Clean up any previous video
      const vUrl = URL.createObjectURL(videoBlob);
      setVideoBlob(videoBlob);
      setVideoUrl(vUrl);

      console.log('Video created successfully');

    } catch (err) {
      console.error('Video creation error:', err);
      setErrorMessage(`Video creation failed: ${err.message}`);
    } finally {
      setIsCreatingVideo(false);
    }
  };

  // Generate AI Voice using ElevenLabs API
  const generateAIVoice = async (text) => {
    // Clean up previous audio
    cleanupAudio();

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setIsGenerating(true);
    setAiStatus('generating');
    setErrorMessage('');

    try {
      const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${INDIAN_VOICE_ID}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': ELEVENLABS_API_KEY
        },
        body: JSON.stringify({
          text: text,
          model_id: 'eleven_turbo_v2',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.75,
            style: 0.0,
            use_speaker_boost: true
          }
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setIsGenerating(false);
        throw new Error(errorData.detail?.message || `API Error: ${response.status}`);
      }
      setIsGenerating(false);
      const audioBlob = await response.blob();

      // Check if blob is valid
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('Received empty audio data');
      }

      const newAudioUrl = URL.createObjectURL(audioBlob);

      // Set audio source and wait for it to load
      audioRef.current.src = newAudioUrl;

      return new Promise((resolve, reject) => {
        const onLoaded = () => {
          setAudioBlob(audioBlob);
          setAudioUrl(newAudioUrl);
          setAudioIsObjectUrl(true);
          setAiStatus('generated');
          setGenerationId(Date.now().toString());
          cleanupListeners();
          resolve(newAudioUrl);
          // Automatically create a one-frame video with the generated audio and message text
          try {
            createVideoFromAudio(text);
          } catch (e) {
            console.warn('Auto video creation failed:', e);
          }
        };

        const onErr = () => {
          cleanupListeners();
          reject(new Error('Failed to load audio'));
        };

        const timeoutId = setTimeout(() => {
          cleanupListeners();
          reject(new Error('Audio loading timeout'));
        }, 10000);

        const cleanupListeners = () => {
          clearTimeout(timeoutId);
          audioRef.current.removeEventListener('loadeddata', onLoaded);
          audioRef.current.removeEventListener('error', onErr);
        };

        audioRef.current.addEventListener('loadeddata', onLoaded);
        audioRef.current.addEventListener('error', onErr);
      });

    } catch (error) {
      console.error('AI Voice generation error:', error);

      if (error.name === 'AbortError') {
        setErrorMessage('Generation cancelled');
      } else {
        setErrorMessage(`Failed to generate audio: ${error.message}`);
        setAiStatus('error');
      }
      throw error;
    } finally {
      setIsGenerating(false);
      abortControllerRef.current = null;
    }
  };

  // Play audio with proper loading
  const playAudio = async () => {
    if (isGenerating) return;

    try {
      // Play AI-generated audio
      if (!audioUrl) {
        setErrorMessage('Please generate audio first');
        return;
      }

      if (audioRef.current.readyState < 3) {
        await new Promise((resolve, reject) => {
          audioRef.current.oncanplaythrough = resolve;
          audioRef.current.onerror = reject;
          setTimeout(reject, 5000);
        });
      }

      await audioRef.current.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('Play audio error:', error);
      setErrorMessage(`Cannot play audio: ${error.message}`);
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }

    // Cancel any ongoing generation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  // Form submit should explicitly create AI audio
  const handleSubmit = async (e) => {
    e.preventDefault();
    const message = generateMessage();
    await generateAIVoice(message);
  };

  const downloadAudio = async () => {
    if (audioBlob) {
      try {
        const url = URL.createObjectURL(audioBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Pediatrician_Day_${doctorName.replace(/\s+/g, '_')}.mp3`;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);

      } catch (error) {
        setErrorMessage(`Download failed: ${error.message}`);
      }
    } else {
      setErrorMessage('Please generate audio first');
    }
  };

  const downloadVideo = async () => {
    if (videoBlob) {
      try {
        const url = URL.createObjectURL(videoBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Pediatrician_Day_${doctorName.replace(/\s+/g, '_')}.webm`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }, 100);
      } catch (error) {
        setErrorMessage(`Video download failed: ${error.message}`);
      }
    } else {
      setErrorMessage('Please create video first');
    }
  };

  const regenerateAudio = async () => {
    stopAudio();
    cleanupAudio();
    cleanupVideo();
    setGenerationId('');
    setErrorMessage('');

    const message = generateMessage();
    await generateAIVoice(message);
  };

  // Audio player event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const handleError = (e) => {
      console.error('Audio element error:', e);
      //setErrorMessage('Audio playback error. Please try regenerating.');
      setIsPlaying(false);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
      cleanupVideo();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Initialize audio element with default URL on mount
  useEffect(() => {
    if (audioRef.current && audioUrl) {
      audioRef.current.src = audioUrl;
      audioRef.current.preload = 'auto';
    }
  }, []);

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <div className="logo-icon-container">
              <FaRobot className="logo-ai-icon" />
              <FaChild className="logo-child-icon" />
            </div>
            <div>
              <h1>Pediatrician's Day Greetings</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Error Message */}
      {errorMessage && (
        <div className="error-banner">
          <FaExclamationTriangle />
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="close-error">
            ×
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          {/* Left Column - Input Form */}
          <div className="form-section">
            <div className="card input-card">
              <div className="card-header">
                <FaHospital className="card-icon" />
                <h2>Greeting Details</h2>
              </div>

              <form onSubmit={handleSubmit} className="form">
                <div className="form-group">
                  <label htmlFor="doctorName">
                    <FaUserMd className="input-icon" />
                    Pediatrician's Name
                  </label>
                  <input
                    type="text"
                    id="doctorName"
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                    placeholder="e.g., Dr. Rajesh Sharma"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="brandName">
                    <FaBuilding className="input-icon" />
                    Hospital/Organization
                  </label>
                  <input
                    type="text"
                    id="brandName"
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="e.g., Apollo Children's Hospital"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="divisionName">
                    <FaHospital className="input-icon" />
                    Department/Division
                  </label>
                  <input
                    type="text"
                    id="divisionName"
                    value={divisionName}
                    onChange={(e) => setDivisionName(e.target.value)}
                    placeholder="e.g., Pediatric Cardiology"
                    className="form-input"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-ai generate-btn"
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <FaSpinner className="spinning" />
                      Generating AI Voice...
                    </>
                  ) : (
                    <>
                      <FaRobot />
                      Generate Audio
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Right Column - Preview & Controls */}
          <div className="preview-section">
            <div className="card preview-card">
              <div className="card-header">
                <FaVolumeUp className="card-icon" />
                <h2>Audio Preview</h2>
              </div>

              {/* Status */}
              <div className="ai-status-container">
                <div className="status-indicators">
                  <div className={`status-item ${aiStatus === 'ready' ? 'active' : ''}`}>
                    <div className="status-dot"></div>
                    <span>Ready</span>
                  </div>
                  <div className={`status-item ${aiStatus === 'generating' ? 'active' : ''}`}>
                    <div className="status-dot"></div>
                    <span>Processing</span>
                  </div>
                  <div className={`status-item ${aiStatus === 'generated' ? 'active' : ''}`}>
                    <div className="status-dot"></div>
                    <span>Complete</span>
                  </div>
                </div>

                {aiStatus === 'generating' && (
                  <div className="generating-status">
                    <div className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <div className="loading-text">Generating audio...</div>
                  </div>
                )}

                {aiStatus === 'generated' && (
                  <div className="success-message">
                    <FaCheckCircle /> Audio generated successfully
                  </div>
                )}

                {aiStatus === 'error' && (
                  <div className="error-status">
                    <FaExclamationTriangle /> Generation failed
                  </div>
                )}
              </div>
              {/* Message Preview */}
              <div className="message-preview-container">
                <div className="message-header">
                  <div className="message-badge">
                    <FaRobot /> Message Preview
                  </div>
                </div>

                <div className="message-content">
                  <div className="salutation">
                    Dear {doctorName},
                  </div>

                  <div className="message-body">
                    <p>
                      On this <span className="highlight">Pediatrician's Day</span>, we extend our heartfelt gratitude
                      for the exceptional care, compassion, and dedication you bring to every child's life.
                    </p>

                    <p>
                      Your unwavering commitment shapes <span className="highlight">healthier futures</span>,
                      one precious smile at a time.
                    </p>
                  </div>

                  <div className="message-closing">
                    <p>Warm wishes on Pediatrician's Day,</p>
                    <div className="signature">
                      <div className="signature-line">from</div>
                      <div className="organization-name">{brandName}</div>
                      <div className="division-name">{divisionName}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Audio Player */}
              <div className="audio-player-container">
                <div className="player-header">
                  <h3>Audio Player</h3>
                </div>

                <div className="player-controls">
                  <div className="player-buttons">
                    <button
                      onClick={playAudio}
                      className="btn btn-success play-button"
                      disabled={isGenerating || isPlaying || !audioUrl}
                    >
                      <FaPlay /> {isPlaying ? 'Playing...' : 'Play Audio'}
                    </button>

                    <button
                      onClick={stopAudio}
                      className="btn btn-danger stop-button"
                      disabled={!isPlaying}
                    >
                      <FaStop /> Stop
                    </button>

                    <button
                      onClick={regenerateAudio}
                      className="btn btn-secondary regenerate-button"
                      disabled={isGenerating}
                    >
                      <FaSync /> Regenerate
                    </button>
                  </div>

                  <div className="audio-visualizer">
                    {isPlaying ? (
                      <div className="visualizer-bars">
                        {[...Array(15)].map((_, i) => (
                          <div
                            key={i}
                            className="visualizer-bar"
                            style={{
                              height: `${20 + Math.random() * 70}%`,
                              animationDelay: `${i * 0.03}s`
                            }}
                          ></div>
                        ))}
                      </div>
                    ) : (
                      <div className="audio-placeholder">
                        <FaVolumeUp />
                        <span>
                          {aiStatus === 'generated' ? 'Audio ready to play' :
                            aiStatus === 'generating' ? 'Generating audio...' :
                              'Generate audio to preview'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="download-section">
                    <div className="download-buttons-row">
                      <button
                        onClick={downloadAudio}
                        className="btn btn-primary download-button"
                        disabled={!audioBlob || isGenerating}
                      >
                        <FaDownload /> Download MP3
                      </button>

                      <button
                        onClick={() => createVideoFromAudio(generateMessage())}
                        className="btn btn-info create-video-button"
                        disabled={isCreatingVideo || (!audioBlob && !audioUrl)}
                      >
                        {isCreatingVideo ? <FaSpinner className="spinning" /> : 'Create Video'}
                      </button>

                      <button
                        onClick={downloadVideo}
                        className="btn btn-success download-video-button"
                        disabled={!videoBlob}
                      >
                        Download Video
                      </button>
                    </div>
                    <div className="file-info">
                      {videoBlob ? 'Video ready' : (audioBlob ? 'Create a video' : 'Generate audio first')}
                    </div>
                  </div>
                </div>

                {/* Hidden audio element */}
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  preload="auto"
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={() => setIsPlaying(false)}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;